import { message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, Form, FormGroup, Modal, ModalBody, Spinner } from "reactstrap";
import { AllowAccess } from "../../../components/BlockAccess";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { TicketCategoryTable } from "../../../components/table/SpecialTable";
import { authHeader } from "../../../header";
import { useDebounce } from "../../../hooks/useDebounceSearch";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";

export const TicketCategoryManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "",
  });
  const [editId, setEditId] = useState();
  const [isLoadBtn, setIsLoadBtn] = useState(false);
  const [modal, setModal] = useState({ add: false, edit: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(7);
  const [categories, setCategories] = useState([]);
  const [, setDetails] = useState();
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const debouncedSearchTerm = useDebounce(search, 500);

  useEffect(
    () => {
      // if (debouncedSearchTerm) {
      getAllTicketCategory();
      setCurrentPage(1);
      // }v
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  const onEditClick = async (items) => {
    setModal({ edit: true });
    setFormData({
      name: items.name,
      status: items.status,
      description: items.description,
      id: items.id,
    });
    setDetails(items);
    setEditId(items.id);
  };

  const onInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getAllTicketCategory = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/auth/feedback/categories?platform=TREASURY&search=${search}`,
        {
          headers: authHeader(),
        }
      );
      const { data } = response;
      if (data) {
        setCategories(data);
        setIsLoading(false);
      }
      return { formData };
    } catch (error) {
      setIsLoading(false);
    }
  };

  const addCategory = async (obj) => {
    try {
      setIsLoadBtn(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/feedback/create-category?admin-usage=ADMIN_TREASURY`,
        obj,
        {
          headers: authHeader(),
        }
      );
      const formData = await response.data;
      if (formData) {
        message.success({
          content: "Ticket Category Created Successfully",
          style: {
            marginTop: "20px",
          },
        });
        getAllTicketCategory();
        setIsLoadBtn(false);
        setIsLoading(false);
        setModal({ add: false });
        resetForm();
      }
    } catch (error) {
      const errMsg = error.response;
      message.error({
        content: errMsg.data.message,
        style: {
          marginTop: "20px",
        },
      });
      setIsLoadBtn(false);
      setIsLoading(false);
      setModal({ add: false });
      resetForm();
    }
  };

  const editTicketCategory = async (obj) => {
    try {
      setIsLoadBtn(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/feedback/edit-category?platform=TREASURY`,
        { ...obj, id: editId },
        {
          headers: authHeader(),
        }
      );
      const formData = await response.data;
      if (formData) {
        message.success({
          content: "Ticket Category Updated Successfully",
          style: {
            marginTop: "20px",
          },
        });
        getAllTicketCategory();
        setIsLoadBtn(false);
        setIsLoading(false);
        setModal({ edit: false });
        resetForm();
      }
    } catch (error) {
      const errMsg = error.response;
      message.error({
        content: errMsg.data.message,
        style: {
          marginTop: "20px",
        },
      });
      setIsLoadBtn(false);
      setIsLoading(false);
      setModal({ edit: false });
      resetForm();
    }
  };

  const onFormSubmit = () => {
    addCategory({ ...formData, platform: "TREASURY" });
    resetForm();
    setModal({ add: false });
  };

  const onEditSubmit = () => {
    editTicketCategory(formData);
    resetForm();
    setModal({ edit: false });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      status: "",
    });
  };

  const onFormCancel = () => {
    setModal({ edit: false, add: false });
    resetForm();
  };

  const { errors, register, handleSubmit } = useForm();

  useEffect(() => {
    getAllTicketCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = categories?.slice(indexOfFirstItem, indexOfLastItem);

  // Change Page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <React.Fragment>
      <Head title="Ticket Category"></Head>
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">Ticket Category</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <div className="mr-2">
                    <input
                      className="form-control"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search Ticket Category..."
                    />
                  </div>
                  <AllowAccess allowedRights={["CREATE_TICKET_CATEGORY"]}>
                    <Button color="primary" onClick={() => setModal({ add: true })}>
                      <Icon name="plus"></Icon>
                      <span>Add Ticket Category</span>
                    </Button>
                  </AllowAccess>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>
          <Card className="card-bordered card-preview">
            {isLoading ? (
              <Spinner
                color="primary"
                style={{
                  height: "3rem",
                  width: "3rem",
                  margin: "0 auto",
                }}
              />
            ) : (
              <TicketCategoryTable data={currentItems} modal={modal} onEditClick={onEditClick} />
            )}
          </Card>
          <div className="card-inner">
            {categories && categories.length > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={categories?.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No Categories found</span>
              </div>
            )}
          </div>
        </Block>

        <Modal isOpen={modal.add} toggle={() => setModal({ add: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Add Ticket Category</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onFormSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Category Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Category Name"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.name && <span className="invalid">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        onChange={(e) => onInputChange(e)}
                        ref={register({
                          required: "This field is required",
                        })}
                      >
                        <option value="">Select Status</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                      {errors.status && <span className="invalid">{errors.status.message}</span>}
                      {/* <label className="form-label">Status</label> */}
                      {/* <RSelect options={status} onChange={(e) => setFormData({ ...formData, status: e.value })} /> */}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <FormGroup>
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        defaultValue={formData.description}
                        placeholder="description"
                        onChange={(e) => onInputChange(e)}
                        className="form-control-xl form-control no-resize"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.description && <span className="invalid">{errors.description.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {isLoadBtn ? <Spinner size="sm" /> : "Submit"}
                        </Button>
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modal.edit} toggle={() => setModal({ edit: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Edit Ticket Category</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onEditSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Category Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        placeholder="Category Name"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.name && <span className="invalid">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={(e) => onInputChange(e)}
                        ref={register({
                          required: "This field is required",
                        })}
                      >
                        <option value="">Select Status</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                      {errors.status && <span className="invalid">{errors.status.message}</span>}
                      {/* <label className="form-label">Status</label> */}
                      {/* <RSelect options={status} onChange={(e) => setFormData({ ...formData, status: e.value })} /> */}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <FormGroup>
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        defaultValue={formData.description}
                        placeholder="description"
                        onChange={(e) => onInputChange(e)}
                        className="form-control-xl form-control no-resize"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.description && <span className="invalid">{errors.description.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {isLoadBtn ? <Spinner size="sm" /> : "Submit"}
                        </Button>
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
